import React, { useState, useMemo } from "react";
import { useTableData } from "../../hooks/useTableData";
import { rechargeReportsConfig } from "../../config/tableConfigs";
import { ReceiptModal } from "./ReceiptPage";
import { fetchWrapper } from "utils/apiFetch";
import { useSession } from "hooks/useSession";
import { showMainToast } from "utils/showToast";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const RechargeReportsPage = () => {
  const [showModal, setShowModal] = useState(false);
  const [receiptData, setReceiptData] = useState(null);
  const [loading, setloading] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);
  const { userid, role } = useSession();
  const [refreshKey, setRefreshKey] = useState(0);

  const userRole = role === "Platform User" ? "Admin" : role;

  const getTodayRange = () => {
    const now = new Date();
    const offset = now.getTimezoneOffset();
    const local = new Date(now.getTime() - offset * 60 * 1000);
    const today = local.toISOString().split("T")[0];
    return { start: today, end: today };
  };

  const { start, end } = getTodayRange();

  const [startDate, setStartDate] = useState(start);
  const [endDate, setEndDate] = useState(end);

  const [apiUrl, setApiUrl] = useState(
    `https://pro.mokshiq.in/get_recharge_report?user=${userid}&filter_date=${start}&end_date=${end}`
  );

  const tableData = useTableData(apiUrl, rechargeReportsConfig, refreshKey);

  const {
    loading: tableLoading,
    error: tableError,
    searchTerm,
    setSearchTerm,
    currentPage,
    setCurrentPage,
    itemsPerPage,
    setItemsPerPage,
    sortField,
    sortDirection,
    handleSort,
    selectedRows,
    handleRowSelect,
    handleSelectAll,
    filters,
    handleFilterChange,
    paginatedData,
    sortedData,
    totalPages,
    data
  } = tableData;

  // ✅ VIEW RECEIPT HANDLER
  const handleView = async (report) => {
    try {
      setloading(true);
      if (!report?.id) {
        console.warn("⚠️ No ID found in selected row:", report);
        setloading(false);
        return;
      }

      const url = `https://pro.mokshiq.in/show_receipt_recharge?id=${report.id}`;
      const response = await fetchWrapper(url);

      if (!response || !response.data) {
        console.error("No receipt data received for:", report.id);
        setloading(false);
        return;
      }

      setReceiptData(response.data);
      setShowModal(true);
    } catch (error) {
      console.error("Error fetching receipt:", error);
    } finally {
      setloading(false);
    }
  };

  // ✅ STATUS CHECK HANDLER
  const handleStatusCheck = async (report) => {
    try {
      setStatusLoading(true);
      if (!report?.id) {
        console.warn("⚠️ No ID found in selected row:", report);
        setStatusLoading(false);
        return;
      }

      const url = `https://pro.mokshiq.in/recharge_status_check?id=${report.id}`;
      const response = await fetchWrapper(url);

      if (!response) {
        setStatusLoading(false);
        return;
      }

      if (typeof response.data === "string") {
        showMainToast(response.data, response.status ? "success" : "danger");
        if (response.status === true) {
          setRefreshKey((prev) => prev + 1);
        }
      } else {
        showMainToast("Status checked successfully", "success");
        setRefreshKey((prev) => prev + 1);
      }
    } catch (error) {
      console.error("Error checking status:", error);
      showMainToast("Failed to check status", "danger");
    } finally {
      setStatusLoading(false);
    }
  };

  // ✅ DATE FILTER SUBMISSION HANDLER
  const handleDateSubmit = (e) => {
    if (e) e.preventDefault();
    const newUrl = `https://pro.mokshiq.in/get_recharge_report?user=${userid}&filter_date=${startDate}&end_date=${endDate}`;
    setApiUrl(newUrl);
    setRefreshKey((prev) => prev + 1);
  };

  // ✅ ROLE BASED COLUMN FILTERING
  const filteredConfig = useMemo(() => {
    const baseConfig = { ...rechargeReportsConfig };

    const roleColumnMap = {
      Retailer: [
        "id", "txn_id", "opid", "operator", "product", "number", "amount", "status",
        "Opening Balance BC", "BC Commission", "BC TDS", "Closing Balance BC",
        "created_at", "api_txn_id", "r_type", "order_id"
      ],
      "API Partner": [
        "id", "txn_id", "opid", "operator", "product", "number", "amount", "status",
        "Opening Balance BC", "BC Commission", "BC TDS", "Closing Balance BC",
        "created_at", "api_txn_id", "r_type", "order_id", "api_response"
      ],
      Admin: [
        "id", "txn_id", "BC Name", "Phone", "operator", "product", "number",
        "amount", "status", "Opening Balance", "Commission", "TDS", "Charge",
        "GST", "Closing Balance", "Opening Balance BC", "BC Commission", "BC TDS",
        "Closing balance BC", "Transection Flow", "Wallet Txn", "TXN ID",
        "api_response", "created_at"
      ],
      "Main Admin": [
        "id", "txn_id", "BC Name", "Phone", "operator", "product", "number",
        "amount", "status", "Opening Balance MAdmin", "Commission", "Charge",
        "TDS", "Closing balance MAdmin", "api_response", "created_at"
      ],
      "White label": [
        "id", "txn_id", "BC Name", "Phone", "operator", "number", "amount",
        "status", "Opening Balance", "Commission", "TDS", "Charge", "GST",
        "Closing Balance", "api_response", "created_at"
      ]
    };

    const allowed = roleColumnMap[userRole] || [];
    baseConfig.columns = rechargeReportsConfig.columns.filter((col) =>
      allowed.includes(col.key)
    );
    return baseConfig;
  }, [userRole]);

  // ✅ EXPORT EXCEL HANDLER
  const handleExport = (type = "excel") => {
    try {
      let exportData =
        selectedRows.length > 0
          ? data.filter((item) =>
              selectedRows.includes(item.id || item[filteredConfig.idField])
            )
          : sortedData;

      if (!exportData || exportData.length === 0) {
        alert("No data available to export");
        return;
      }

      const formattedData = exportData.map((row) => {
        const obj = {};
        filteredConfig.columns.forEach((col) => {
          obj[col.label] = row[col.key];
        });
        return obj;
      });

      if (type === "excel") {
        const ws = XLSX.utils.json_to_sheet(formattedData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Recharge Reports");
        const buffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
        saveAs(
          new Blob([buffer]),
          `Recharge_Reports_${startDate}_to_${endDate}.xlsx`
        );
      }
    } catch (err) {
      console.error("Export error:", err);
      alert("Export failed");
    }
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined || amount === "") return "N/A";
    const num = parseFloat(amount);
    if (isNaN(num)) return "N/A";
    return `₹${num.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  };

  const formatCellValue = (value, column) => {
    if (value === null || value === undefined || value === "") return "N/A";
    if (column.type === "currency") return formatCurrency(value);
    if (column.type === "badge") {
      const bgClass = column.badgeClass?.(value) || "bg-secondary";
      return (
        <span
          className={`badge ${bgClass} rounded-pill px-2.5 py-1 fw-semibold`}
          style={{ fontSize: "11px" }}
        >
          {column.transform
            ? column.transform(value)
            : String(value).toUpperCase()}
        </span>
      );
    }
    return column.transform ? column.transform(value) : String(value);
  };

  const totalRecordsCount = sortedData?.length || 0;
  const startItem =
    totalRecordsCount > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0;
  const endItem = Math.min(currentPage * itemsPerPage, totalRecordsCount);

  return (
    <main>
      <div className="container-fluid">
        {(loading || tableLoading || statusLoading) && (
          <div
            className="position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center"
            style={{ backgroundColor: "rgba(255, 255, 255, 0.75)", zIndex: 4000 }}
          >
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        )}

        {/* Branded Hero Banner Matching Design */}
        <div
          className="card border-0 mb-3 overflow-hidden position-relative shadow-sm"
          style={{
            background: "linear-gradient(135deg, #f5efff 0%, #e0e7ff 100%)",
            borderRadius: "16px"
          }}
        >
          <div className="card-body p-3 px-3.5 position-relative z-1">
            <div className="row align-items-center mb-2">
              <div className="col-12 col-md-8">
                <div className="d-flex align-items-center">
                  <div
                    className="d-flex align-items-center justify-content-center flex-shrink-0 shadow-xs"
                    style={{
                      width: "42px",
                      height: "42px",
                      borderRadius: "12px",
                      backgroundColor: "#7e57c2",
                      color: "#ffffff",
                      marginRight: "18px"
                    }}
                  >
                    <i
                      className="ph-duotone ph-device-mobile-camera"
                      style={{ fontSize: "22px" }}
                    ></i>
                  </div>
                  <div>
                    <h5
                      className="fw-extrabold mb-0.5"
                      style={{ color: "#0f172a", fontSize: "18px" }}
                    >
                      Recharge Reports
                    </h5>
                    <p className="text-muted mb-0" style={{ fontSize: "12.5px" }}>
                      View and analyze all Mobile & DTH recharge transactions with detailed reports.
                    </p>
                  </div>
                </div>
              </div>

              <div className="col-12 col-md-4 text-end d-none d-md-block">
                <img
                  src="/assets/images/recharge_report_3d.png"
                  alt="Recharge Reports 3D Illustration"
                  style={{
                    maxHeight: "85px",
                    objectFit: "contain",
                    filter: "drop-shadow(0px 6px 12px rgba(126, 87, 194, 0.18))"
                  }}
                />
              </div>
            </div>

            {/* Date Filter Integrated Floating Bar */}
            <form onSubmit={handleDateSubmit}>
              <div
                className="bg-white p-2 px-3 rounded-3 shadow-xs border border-light-subtle d-flex flex-wrap align-items-center gap-2 justify-content-between"
                style={{ borderRadius: "12px" }}
              >
                <div className="d-flex flex-wrap align-items-center gap-2.5 flex-grow-1">
                  <div
                    className="d-flex flex-column flex-grow-1"
                    style={{ minWidth: "160px", maxWidth: "240px" }}
                  >
                    <label
                      className="form-label text-secondary fw-semibold mb-0.5"
                      style={{ fontSize: "11px" }}
                    >
                      From Date
                    </label>
                    <div className="position-relative">
                      <input
                        type="date"
                        className="form-control rounded-2 border-light-subtle shadow-xs pe-2 py-1"
                        style={{
                          fontSize: "12.5px",
                          height: "34px",
                          backgroundColor: "#f8fafc"
                        }}
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        required
                      />
                    </div>
                  </div>

                  <div
                    className="d-flex align-items-center justify-content-center text-muted mx-2"
                    style={{ marginTop: "16px" }}
                  >
                    <div
                      className="rounded-circle d-flex align-items-center justify-content-center"
                      style={{
                        width: "28px",
                        height: "28px",
                        backgroundColor: "#e0e7ff",
                        color: "#7e57c2"
                      }}
                    >
                      <i
                        className="ph-bold ph-arrow-right"
                        style={{ fontSize: "12px" }}
                      ></i>
                    </div>
                  </div>

                  <div
                    className="d-flex flex-column flex-grow-1"
                    style={{ minWidth: "160px", maxWidth: "240px" }}
                  >
                    <label
                      className="form-label text-secondary fw-semibold mb-0.5"
                      style={{ fontSize: "11px" }}
                    >
                      To Date
                    </label>
                    <div className="position-relative">
                      <input
                        type="date"
                        className="form-control rounded-2 border-light-subtle shadow-xs pe-2 py-1"
                        style={{
                          fontSize: "12.5px",
                          height: "34px",
                          backgroundColor: "#f8fafc"
                        }}
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        required
                      />
                    </div>
                  </div>
                </div>

                <div className="mt-2 mt-sm-0 align-self-end">
                  <button
                    type="submit"
                    className="btn text-white fw-bold d-inline-flex align-items-center justify-content-center gap-1.5 px-3.5 shadow-xs"
                    style={{
                      background:
                        "linear-gradient(135deg, #7e57c2 0%, #673ab7 100%)",
                      borderRadius: "10px",
                      height: "34px",
                      minWidth: "120px",
                      fontSize: "13px"
                    }}
                  >
                    <i
                      className="ph-bold ph-paper-plane-tilt"
                      style={{ fontSize: "14px" }}
                    ></i>
                    Submit
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>

        {/* Reports Table Container Card */}
        <div className="card border-0 rounded-4 shadow-sm overflow-hidden mb-4">
          {/* Card Header Matching Design */}
          <div className="card-header bg-white p-3.5 border-bottom border-light-subtle">
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
              <div className="d-flex align-items-center">
                <div
                  className="d-flex align-items-center justify-content-center rounded-3 flex-shrink-0"
                  style={{
                    width: "38px",
                    height: "38px",
                    backgroundColor: "#f3e8ff",
                    color: "#7e57c2",
                    marginRight: "16px"
                  }}
                >
                  <i
                    className="ph-duotone ph-device-mobile-camera"
                    style={{ fontSize: "20px" }}
                  ></i>
                </div>
                <div>
                  <h6 className="fw-bold mb-0 text-dark" style={{ fontSize: "16px" }}>
                    Recharge Reports
                  </h6>
                  <p className="text-muted mb-0" style={{ fontSize: "12.5px" }}>
                    Total {totalRecordsCount} records found
                  </p>
                </div>
              </div>

              {/* Right Header Action Controls */}
              <div className="d-flex align-items-center gap-2 flex-wrap ms-auto">
                <div className="position-relative" style={{ minWidth: "220px" }}>
                  <i
                    className="ph-bold ph-magnifying-glass position-absolute top-50 start-0 translate-middle-y ms-3 text-muted"
                    style={{ fontSize: "14px" }}
                  ></i>
                  <input
                    type="text"
                    className="form-control rounded-pill ps-5 pe-3 py-1.5 border-light-subtle shadow-xs"
                    style={{ fontSize: "13px" }}
                    placeholder="Search by ID, Txn ID, Mobile, etc..."
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setCurrentPage(1);
                    }}
                  />
                </div>

                <select
                  className="form-select rounded-3 py-1.5 px-3 border-light-subtle shadow-xs"
                  style={{ width: "75px", fontSize: "13px" }}
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>

                <button
                  type="button"
                  className="btn p-2 rounded-3 text-white d-flex align-items-center justify-content-center"
                  style={{ width: "36px", height: "36px", backgroundColor: "#7e57c2" }}
                  title="Filter Options"
                >
                  <i className="ph-bold ph-funnel" style={{ fontSize: "16px" }}></i>
                </button>

                <button
                  type="button"
                  className="btn p-2 rounded-3 border border-light-subtle d-flex align-items-center justify-content-center"
                  style={{
                    width: "36px",
                    height: "36px",
                    backgroundColor: "#f8fafc",
                    color: "#7e57c2"
                  }}
                  onClick={() => handleExport("excel")}
                  title="Export Excel"
                >
                  <i
                    className="ph-bold ph-download-simple"
                    style={{ fontSize: "16px" }}
                  ></i>
                </button>
              </div>
            </div>
          </div>

          {/* Table Body & Empty State */}
          <div className="card-body p-0">
            <div className="table-responsive app-scroll">
              <table className="table align-middle mb-0 text-nowrap">
                <thead style={{ backgroundColor: "#f5efff", color: "#334155" }}>
                  <tr>
                    <th style={{ width: "40px" }} className="ps-3">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={
                          selectedRows.length === paginatedData.length &&
                          paginatedData.length > 0
                        }
                        onChange={handleSelectAll}
                      />
                    </th>
                    {filteredConfig.columns.map((column) => (
                      <th
                        key={column.key}
                        onClick={() =>
                          column.sortable !== false && handleSort(column.key)
                        }
                        style={{
                          cursor:
                            column.sortable !== false ? "pointer" : "default",
                          fontSize: "12.5px",
                          fontWeight: "700",
                          color: "#334155",
                          padding: "12px 14px"
                        }}
                      >
                        <div className="d-flex align-items-center gap-1">
                          <span>{column.label}</span>
                          {column.sortable !== false && (
                            <i
                              className="ph-bold ph-caret-up-down text-muted"
                              style={{ fontSize: "11px" }}
                            ></i>
                          )}
                        </div>
                      </th>
                    ))}
                    <th
                      style={{
                        fontSize: "12.5px",
                        fontWeight: "700",
                        color: "#334155",
                        padding: "12px 14px"
                      }}
                    >
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedData && paginatedData.length > 0 ? (
                    paginatedData.map((item) => (
                      <tr
                        key={item.id || item[filteredConfig.idField]}
                        className="border-bottom border-light-subtle"
                      >
                        <td className="ps-3">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={selectedRows.includes(
                              item.id || item[filteredConfig.idField]
                            )}
                            onChange={() =>
                              handleRowSelect(
                                item.id || item[filteredConfig.idField]
                              )
                            }
                          />
                        </td>
                        {filteredConfig.columns.map((column) => (
                          <td
                            key={column.key}
                            style={{ fontSize: "13px", padding: "12px 14px" }}
                          >
                            {formatCellValue(item[column.key], column)}
                          </td>
                        ))}
                        <td style={{ padding: "12px 14px" }}>
                          <div className="d-flex align-items-center gap-1.5">
                            <button
                              type="button"
                              className="btn btn-sm d-inline-flex align-items-center justify-content-center p-1.5 rounded-3 text-white"
                              style={{ backgroundColor: "#7e57c2" }}
                              title="View Receipt"
                              onClick={() => handleView(item)}
                            >
                              <i
                                className="ph-bold ph-eye"
                                style={{ fontSize: "14px" }}
                              ></i>
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm d-inline-flex align-items-center justify-content-center p-1.5 rounded-3 text-white"
                              style={{ backgroundColor: "#f59e0b" }}
                              title="Check Status"
                              onClick={() => handleStatusCheck(item)}
                            >
                              <i
                                className="ph-bold ph-arrows-counter-clockwise"
                                style={{ fontSize: "14px" }}
                              ></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : null}
                </tbody>
              </table>

              {/* Empty State Matching Design */}
              {(!paginatedData || paginatedData.length === 0) && (
                <div className="text-center py-5 px-3">
                  <div className="mb-3 d-inline-flex align-items-center justify-content-center position-relative">
                    <div
                      className="d-flex align-items-center justify-content-center rounded-circle"
                      style={{
                        width: "72px",
                        height: "72px",
                        backgroundColor: "#f3e8ff",
                        color: "#7e57c2"
                      }}
                    >
                      <i
                        className="ph-duotone ph-file-search"
                        style={{ fontSize: "36px" }}
                      ></i>
                    </div>
                  </div>
                  <h6 className="fw-bold text-dark mb-1" style={{ fontSize: "16px" }}>
                    No records found
                  </h6>
                  <p className="text-muted mb-3" style={{ fontSize: "13px" }}>
                    Try adjusting your search or filter criteria
                  </p>
                  <div className="d-flex justify-content-center w-100">
                    <button
                      type="button"
                      className="btn rounded-pill px-3.5 py-1.5 fw-semibold d-inline-flex align-items-center justify-content-center gap-2"
                      style={{
                        border: "1px solid #d8b4fe",
                        backgroundColor: "#fdf4ff",
                        color: "#7e57c2",
                        fontSize: "13px",
                        width: "auto",
                        maxWidth: "180px",
                        display: "inline-flex"
                      }}
                      onClick={() => {
                        setSearchTerm("");
                        const { start, end } = getTodayRange();
                        setStartDate(start);
                        setEndDate(end);
                        const newUrl = `https://pro.mokshiq.in/get_recharge_report?user=${userid}&filter_date=${start}&end_date=${end}`;
                        setApiUrl(newUrl);
                        setRefreshKey((prev) => prev + 1);
                      }}
                    >
                      <i
                        className="ph-bold ph-arrows-counter-clockwise"
                        style={{ fontSize: "14px" }}
                      ></i>
                      Clear Filters
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Table Footer Matching Design */}
          <div className="card-footer bg-white p-3 border-top border-light-subtle d-flex flex-wrap align-items-center justify-content-between gap-3">
            <span className="text-muted" style={{ fontSize: "13px" }}>
              Showing {startItem} to {endItem} of {totalRecordsCount} entries
            </span>

            <div className="d-flex align-items-center gap-2">
              <button
                type="button"
                className="btn btn-sm rounded-pill px-3 py-1.5 border border-light-subtle fw-semibold d-inline-flex align-items-center gap-1"
                style={{
                  fontSize: "12.5px",
                  backgroundColor: currentPage === 1 ? "#f8fafc" : "#f1f5f9",
                  color: currentPage === 1 ? "#94a3b8" : "#7e57c2"
                }}
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
              >
                <i className="ph-bold ph-caret-left" style={{ fontSize: "12px" }}></i>
                Previous
              </button>

              <button
                type="button"
                className="btn btn-sm rounded-pill px-3 py-1.5 border border-light-subtle fw-semibold d-inline-flex align-items-center gap-1"
                style={{
                  fontSize: "12.5px",
                  backgroundColor:
                    currentPage >= totalPages || totalPages === 0
                      ? "#f8fafc"
                      : "#f1f5f9",
                  color:
                    currentPage >= totalPages || totalPages === 0
                      ? "#94a3b8"
                      : "#7e57c2"
                }}
                disabled={currentPage >= totalPages || totalPages === 0}
                onClick={() =>
                  setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                }
              >
                Next
                <i
                  className="ph-bold ph-caret-right"
                  style={{ fontSize: "12px" }}
                ></i>
              </button>
            </div>
          </div>
        </div>

        {/* Recharge Receipt Modal */}
        <ReceiptModal
          receiptData={receiptData}
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          title="Recharge Receipt"
          logoUrl={"assets/images/logo/2.png"}
        />
      </div>
    </main>
  );
};

export default RechargeReportsPage;
